import { t } from '../i18n/index.js';
import { isoDateLabel } from '../domain/trendingDigest.js';

/**
 * Render the "Trending Digest" panel into `container`.
 *
 * Shows the top movers from the user's watchlist based on rank-snapshot deltas.
 * Clicking an app card calls `onAppClick(trackId)`.
 *
 * @param {import('../domain/trendingDigest.js').AppMover[]} movers
 * @param {HTMLElement} container
 * @param {(trackId: string) => void} onAppClick
 */
export function renderTrendingDigest(movers, container, onAppClick) {
  if (!container) return;

  if (!movers || movers.length === 0) {
    container.hidden = true;
    container.innerHTML = '';
    return;
  }

  const items = movers.slice(0, 10).map((mover) => {
    const isUp = mover.netDelta > 0;
    const arrow = isUp ? '▲' : '▼';
    const dirClass = isUp ? 'trending-mover--up' : 'trending-mover--down';
    const deltaText = (isUp ? '+' : '') + mover.netDelta.toFixed(1);
    const countText = isUp
      ? t('digest.countriesUp', { n: mover.countriesUp })
      : t('digest.countriesDown', { n: mover.countriesDown });
    const artSrc = mover.artworkUrl
      ? mover.artworkUrl.replace('100x100', '60x60')
      : '';

    return `<li class="trending-mover ${dirClass}" role="button" tabindex="0"
        data-track-id="${mover.trackId}"
        aria-label="${mover.trackName}: ${arrow} ${deltaText}">
      ${artSrc ? `<img class="trending-mover__art" src="${artSrc}" alt="" loading="lazy" width="40" height="40">` : ''}
      <span class="trending-mover__name">${mover.trackName}</span>
      <span class="trending-mover__delta" aria-hidden="true">${arrow} ${deltaText}</span>
      <span class="trending-mover__countries">${countText}</span>
    </li>`;
  });

  const fromDate = isoDateLabel(movers[0]?.snapshotFrom ?? '');
  const subtitle = fromDate ? t('digest.since', { date: fromDate }) : '';

  container.innerHTML = `
    <details class="trending-digest" open>
      <summary class="trending-digest__summary">
        <span class="trending-digest__title">${t('digest.title')}</span>
        ${subtitle ? `<span class="trending-digest__subtitle">${subtitle}</span>` : ''}
        <span class="trending-digest__count">${movers.length}</span>
      </summary>
      <ul class="trending-digest__list">${items.join('')}</ul>
    </details>
  `.trim();

  container.hidden = false;

  container.querySelectorAll('.trending-mover[data-track-id]').forEach((el) => {
    const handler = () => {
      if (onAppClick) onAppClick(el.dataset.trackId);
    };
    el.addEventListener('click', handler);
    el.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handler();
      }
    });
  });
}
